import { useCallback, useMemo, useRef } from 'react';
import { useAddMessage, useMessageQueueActions } from '@react-md/alert';
import { useToggle } from '@react-md/utils';
import { useAppSelector } from '@app/hooks';
import { selectAppCompanyName, selectAppSettings } from '@modules/app';
import { SsoEnabled } from '@services/df/codegen-enums';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import {
    TwoFactorResponse,
    UserLoginOptions,
    useAuthLoginOptionsCreateMutation,
    useObtainAuthTokenCreateMutation,
    usePostV22FaSetupMutation,
    usePostV22FaVerifyMutation,
} from '@services/df/auth';
// import { createToast as toast } from '@shared/components/toast';
import { isFetchBaseQueryError } from '@utils/http';
import { SignInOptionsForm, SignInForm, TwoFactorForm } from './types';

const getSignInOptions = ({ available_login_options }: UserLoginOptions) => {
    const hasOneMethod = available_login_options.length === 1;
    const hasPasswordMethod = available_login_options.includes('password');
    const hasSsoMethod = available_login_options.includes('sso');

    return { hasSsoOnly: hasOneMethod && hasSsoMethod, hasPasswordMethod, hasSsoMethod };
};

export const useSignInFormFlow = ({ isSsoEnabled }: { isSsoEnabled: boolean }) => {
    const [isBaseForm, showBaseForm, hideBaseForm] = useToggle(!isSsoEnabled);

    return {
        isBaseForm,
        showBaseForm,
        hideBaseForm,
    };
};

export const useSignInSettings = () => {
    const settings = useAppSelector(selectAppSettings);
    const isStaticConfiguration = settings.login_configuration === 'static';
    const isDynamicConfiguration = settings.login_configuration === 'dynamic';
    const isSsoEnabled =
        isStaticConfiguration &&
        (settings.sso_enabled === SsoEnabled.Widget || settings.sso_enabled === SsoEnabled.BackofficeAndWidget);

    return {
        isStaticConfiguration,
        isDynamicConfiguration,
        slug: settings.company_title,
        title: settings.company_full_title || settings.company_title,
        isSsoEnabled,
    };
};

const hasTwoFactor = (error: FetchBaseQueryError) => error?.status === 428;

const getTwoFactorStage = (error: unknown) => {
    if (isFetchBaseQueryError(error) && hasTwoFactor(error)) {
        const response: TwoFactorResponse = error?.data; // {"next_action":"verify_2fa","two_fa_type":"email"}
        const shouldSetup2Fa = response.next_action === 'setup_2fa';
        const shouldVerify2Fa = response.next_action === 'verify_2fa';
        return {
            shouldSetup2Fa,
            shouldVerify2Fa,
            twoFactorType: response.two_fa_type,
        };
    }
};

export const useAuthSignIn = () => {
    const addMessage = useAddMessage();

    const signInOptionsForm = useRef<SignInOptionsForm>();
    const signInForm = useRef<SignInForm>();
    const companySlug = useRef<string>();

    const [_getSignInOptions, { data: signInOptions, isLoading: isSignInOptionsLoading, reset: resetSignInOptions }] =
        useAuthLoginOptionsCreateMutation();
    const [_signInCompany, { isLoading: isTokenLoading, error }] = useObtainAuthTokenCreateMutation();
    const [_setup2Fa, { data: config2Fa, isLoading: is2FaLoading, reset: resetConfig2Fa }] =
        usePostV22FaSetupMutation();
    const [_verify2Fa, { data: verify2FaData, isLoading: isVerifying, reset: resetCode }] =
        usePostV22FaVerifyMutation();

    const createGetSignInOptions = useCallback(
        (company_name: string) => async (_form?: SignInOptionsForm) => {
            companySlug.current = company_name;

            if (_form) {
                signInOptionsForm.current = _form;
            }

            const credentials = signInOptionsForm.current;

            if (!credentials) return;

            try {
                await _getSignInOptions({
                    userLoginOptionsRequestRequest: {
                        company_name,
                        ...credentials,
                    },
                }).unwrap();
            } catch (error) {
                // if (isFetchBaseQueryError(error)) {
                //     toast({
                //         status: 'error',
                //         title: error.message,
                //         isClosable: true,
                //     });
                // }

                throw error;
            }
        },
        [_getSignInOptions],
    );

    const { shouldSetup2Fa, shouldVerify2Fa, twoFactorType } = getTwoFactorStage(error) || {};

    const verify = useCallback(
        async (twoFactorForm: TwoFactorForm) => {
            const credentials = signInForm.current;

            if (!companySlug.current || !credentials) return;

            try {
                await _verify2Fa({
                    twoFactorAuthVerifyRequestRequest: {
                        company_name: companySlug.current,
                        ...credentials,
                        ...twoFactorForm,
                    },
                }).unwrap();
            } catch (error) {
                // if (isFetchBaseQueryError(error)) {
                //     toast({
                //         status: 'error',
                //         title: error.message,
                //         isClosable: true,
                //     });
                // }

                throw error;
            }
        },
        [_verify2Fa],
    );

    const createSignInCompany = useCallback(
        (company_name: string) =>
            async (_form?: SignInForm): Promise<{ isSignInSuccess: boolean; error?: FetchBaseQueryError }> => {
                companySlug.current = company_name;

                if (_form) {
                    signInForm.current = _form;
                }

                let isSignInSuccess = false;
                let shouldSetup2Fa = false;
                const credentials = signInForm.current;

                if (!credentials) return { isSignInSuccess };

                try {
                    await _signInCompany({
                        obtainTokenRequestRequest: {
                            company_name,
                            ...credentials,
                        },
                    }).unwrap();

                    isSignInSuccess = true;
                } catch (error) {
                    if (isFetchBaseQueryError(error) && !hasTwoFactor(error)) {
                        // toast({
                        //     status: 'error',
                        //     title: error.message,
                        //     isClosable: true,
                        // });
                        return { isSignInSuccess, error };
                    }

                    shouldSetup2Fa = !!getTwoFactorStage(error)?.shouldSetup2Fa;
                }

                if (!shouldSetup2Fa) return { isSignInSuccess };

                try {
                    await _setup2Fa({
                        twoFactorAuthSetupRequestRequest: {
                            company_name,
                            ...credentials,
                        },
                    }).unwrap();
                } catch (error) {
                    // isFetchBaseQueryError(error) &&
                    //     toast({
                    //         status: 'error',
                    //         title: error.message,
                    //         isClosable: true,
                    //     });
                }

                return { isSignInSuccess };
            },
        [_signInCompany, _setup2Fa],
    );

    return {
        // sign in dynamic
        isSignInOptionsLoading,
        createGetSignInOptions,
        signInOptions: signInOptions ? getSignInOptions(signInOptions) : null,
        signInDynamicForm: signInOptionsForm.current,

        // sign in
        isTokenLoading,
        createSignInCompany,

        // 2Fa
        shouldSetup2Fa,
        shouldVerify2Fa,
        twoFactorType,
        isSetupTwoFactorStep: shouldSetup2Fa,
        isVerifyTwoFactorStep: shouldVerify2Fa,
        is2FaLoading,
        isVerifying,
        verify,
        verify2FaData,
        // verify2FaData: {
        //     token: 'issuer_name',
        //     backup_codes: [
        //         'HU6JG4U7',
        //         '4SPYWH77',
        //         '8ZRG9EVC',
        //         'XYJXX4RQ',
        //         'J5A3SWBW',
        //         '6Z92WXCK',
        //         'L6SGDGET',
        //         'X68EGVPC',
        //         'R2ML36EU',
        //         '778QSB9Z',
        //     ],
        // },
        config2Fa,
        // config2Fa: {
        //     qr_code_data_uri:
        //         'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAeoAAAHqAQAAAADjFjCXAAAEPUlEQVR4nO2dQY6sOAyGP0+QakndoI4CN5gjtfpIcwM4Sh1gJFiWBPpn4QTS/XYNT/16sBeoUPHJlGQ5iX8nZeKAjX8doSHwwAMPPPDAAw888HNxy9Zg/dzAeF+N0cysnxv/FlgN5vJof573wC+Kd5KkCTR4cDVY3y5oaF+mgSTr/ckkSdJH/KD3wC+Kz1v6AqBdoJuSPP+9Tavt35lZc7L3wK+FN5/ujXZpjDaJ0dICpIXxIQzAun9O9R544IBnOGA1ugkfYf12vKdfa3t/2MsH/jPwkutaATMw3ifohhVoX6ZuQjDfZLA2YoY69n70bw/8m/HRzMzuQPds8DDr/dJgbxPQPW+yntXKkvY874FfDPdc92noFPNqjI+XaXwsQPsyxnsi58TTvAd+TRyvg3SS6CaAVpKmJA3tgjSxzfU2azOh4Uf/9sC/C/eok7QgafHK3V45+Rx63YfnIuoC/5qVXFfC7KPl+NMCkKQB8Isi6gL/slXBhYsRCz7MDn6bh95ym5+LXBf4Aavmdfl2ydmsijr/NmXRLA/HEXWBH8VXkyQvlUC7kIsmXkgxk7Rgdk8ys1uRZf+Qlw/8Z+LzTYzWoPeHZD1py3CrQZvDUZpWK3rFid4DvxZe6bDWaTUxNwvM5ipFFiPmO16+AzDaJDvFe+BXxvXuDXU3T24aWkl6lj6U8VFam8bHUpqhzvMe+NXwbQ1bLt1Eqc3t9bpSXNHgUIoqceAHrIyws5WLa14LGh8vUzetAGsDJOGNT39PJ3kP/Jp4yXXsea3US4AiUPgn2LWJqJwEfhR3zYu5AUjyeonLsoC9TUl63/rrxqicBH7MctJq86VSKT4JtD7185wY6n/gJ+H29jSje958SM1bxson6Znrddb/Du+BXwivU1qV8FzpT7UONmxIN4X6H/ghy2WRqcTVrrm6Dkv6MOpOocMGft4adst6E3hXZ57wbf112qt5RM9J4EdMlU2UXrq2NNR5B+dENf4qujoDP4ZXua4U41Q3b27aRA64KW2hF1EX+FetyF15Nsee4XLC26p5rVQFZkRd4Ifx2YxOL9977aWSgZzwzO6gwY/dSXl7Yn+m98CvhW87EwXMdwQv09jvT6QFv23/9T6o2IUd+HmriXywk+pu9bxRUZtoAcS+icBPwfN2fj/YaTU6P8QpVSoFedYHVXHvj3j5wH8ovq9SSwsAmD1eRvc08771vWjnp9ud5z3wa+L7WZ1UC9nnTWb3JO8lHj0d5r08Z3oP/Go49ZRu66XbzpTY53V7bTh02MDP02HLcSapiqsSZls4AlUkRtQF/iX7FHX73n+qdpN2oTp7Z4g1bODn4+Ukk4X9TDu93z+0O/0+74FfBm9Vj6HdtuN/aCXr21yvK42fcRp24IesUv+35WuezaksVX003YbeqkvgR//2wL8Lt/j3usADDzzwwAMPPPD/Bf4fCJnhViyp2jEAAAAASUVORK5CYII=',
        //     secret: 'secret_secret_secret_secret_secret',
        //     issuer_name: 'issuer_name',
        //     account_name: 'account_name',
        // },
        resetSignInOptions,
        resetConfig2Fa,
        resetCode,
    };
};

export const useCompanySignInSubmit = () => {
    const company = useAppSelector(selectAppCompanyName);
    const { createSignInCompany, createGetSignInOptions, ...rest } = useAuthSignIn();
    const signInSubmit = useMemo(() => createSignInCompany(company), [createSignInCompany, company]);
    const getSignInOptions = useMemo(() => createGetSignInOptions(company), [createGetSignInOptions, company]);

    return { getSignInOptions, signInSubmit, ...rest };
};
